import { useEffect, useState, useRef } from "react";
import useDebounce from "./../../customHook/useDebounce";
import Icon from "../../assets/icons/Icon";
import { isMobileView } from "./CommonFunctions";

const Autocompletesearch = ({
  label,
  value,
  onChange,
  onSelect,
  placeholder,
  Addbuttonclick,
  Addbutntext,
  showsearch,
  searchbtnclick,
  errorMessage,
  data = [],
  defaultDataCount = 5,
  filterKey = "text",
  renderSuggestion,
  sideButton,
  sideButtonClick,
  clearInputValueAfterSelect,
  disabled,
  showSideLoader,
  isMandatory,
  infoText,
  tooltipPosition = "bottom",
  tooltipOffset = "0",
  tooltipWidth = null,
  // Scan functionality props (same as Searchbuttonwithbarcodefilter)
  onScan,
}) => {
  const getTooltipPositionClasses = (position) => {
    switch(position) {
      case 'top': return 'bottom-full left-1/2 -translate-x-1/2 mb-2';
      case 'bottom': return 'top-full left-1/2 -translate-x-1/2 mt-2';
      case 'left': return 'right-full top-1/2 -translate-y-1/2 mr-2';
      case 'right': return 'left-full top-1/2 -translate-y-1/2 ml-2';
      default: return 'bottom-full left-1/2 -translate-x-1/2 mb-2';
    }
  };
  
  const [showInfoText, setShowInfoText] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const debouncedValue = useDebounce(inputValue, 500);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [forceShowAll, setForceShowAll] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const wrapperRef = useRef(null);
  const suggestionsRef = useRef(null);
  const itemRefs = useRef([]);
  const [dropdownDirection, setDropdownDirection] = useState('down');
  
  // Scan mode state (same as Searchbuttonwithbarcodefilter)
  const [scanMode, setScanMode] = useState(false);
  const [isScanning, setIsScanning] = useState(false); // New state for scanning loader
  const inputRef = useRef(null);
  let inputRect = "";

  useEffect(() => {
    if (showSuggestions && wrapperRef.current) {
      const inputRect = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;
      
      if (spaceBelow < 250 && spaceAbove > spaceBelow) {
        setDropdownDirection('up');
      } else {
        setDropdownDirection('down');
      }
    }
  }, [showSuggestions]);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Focus input when entering scan mode
  useEffect(() => {
    if (scanMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [scanMode]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setForceShowAll(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef, scanMode]);

  // Reset isTyping when suggestions are closed
  useEffect(() => {
    if (!showSuggestions) {
      setIsTyping(false);
    }
  }, [showSuggestions]);

  const searchValue = debouncedValue.toLowerCase();
  
  // Scan mode में suggestions न दिखाने के लिए condition modify करें
  const filteredData =
  scanMode 
    ? [] // Scan mode में suggestions न दिखाएं
    : (!isTyping && showSuggestions)
      ? data.slice(0, defaultDataCount)
      : debouncedValue
        ? data?.filter((item) => {
            if (Array.isArray(filterKey)) {
              return filterKey.some((key) =>
                String(item[key] || "")
                  .toLowerCase()
                  .includes(searchValue)
              );
            }
            return String(item[filterKey] || "")
              .toLowerCase()
              .includes(searchValue);
          })
        : data?.slice(0, defaultDataCount);

  const getDefaultDisplayValue = (item) => {
    if (Array.isArray(filterKey)) {
      return item[filterKey[0]] || "";
    }
    return item[filterKey] || "";
  };

  // Check if an item matches the current input value
  const isItemSelected = (item) => {
    const displayValue = getDefaultDisplayValue(item);
    return displayValue === inputValue;
  };

  const handleItemClick = (item) => {
    const displayValue = getDefaultDisplayValue(item);
    if (clearInputValueAfterSelect) {
      setInputValue("");
      onChange("");
    } else {
      setInputValue(displayValue);
      onChange(displayValue);
    }
    if (onSelect) {
      onSelect(item);
    }
    setShowSuggestions(false);
    setForceShowAll(false);
  };

  const handleFocus = () => {
    if (scanMode) return; // Scan mode में focus pe suggestions न खोलें
    
    if (clearInputValueAfterSelect) {
      setInputValue(""); // Clear input value when focusing
      onChange(""); // Notify parent of the change
    }
    setShowSuggestions(true);
    setSelectedIndex(-1);
    setForceShowAll(true);
  };

  const handleToggleSuggestions = () => {
    if (scanMode) return; // Scan mode में suggestions न टॉगल करें
    
    const willShow = !showSuggestions;
    setShowSuggestions(willShow);
    setForceShowAll(willShow);
    setSelectedIndex(-1);
    setIsTyping(false); // Reset typing state when toggling suggestions
  };

  // Scan functionality (same as Searchbuttonwithbarcodefilter)
  const toggleScanMode = () => {
    const newScanMode = !scanMode;
    setScanMode(newScanMode);
    
    if (newScanMode) {
      setInputValue(""); // Clear input when entering scan mode
      onChange("");
      setShowSuggestions(false); // Scan mode में suggestions बंद करें
    } else {
      setShowSuggestions(true); // Scan mode से बाहर निकलने पर suggestions खोलें
    }
  };

  const handleKeyDown = (e) => {
    // Handle Enter key for scan mode
    if (e.key === "Enter" && scanMode) {
      if (inputValue.trim()) {
        setIsScanning(true); // Scanning loader show करें
        
        // Simulate scanning delay (आप actual scanning logic से replace करें)
        if(onScan){
              onScan(inputValue);
                setInputValue(""); // Clear input after scan
          setIsScanning(false); // Scanning loader hide करें
        }
        else{
            onChange(inputValue);
            setInputValue(""); // Clear input after scan
          setIsScanning(false); // Scanning loader hide करें
        }
        // setTimeout(() => {
        //   if (onScan) {
        //     onScan(inputValue);
        //   } else {
        //     // Default behavior: search for the scanned value
        //     onChange(inputValue);
        //   }
        //   setInputValue(""); // Clear input after scan
        //   setIsScanning(false); // Scanning loader hide करें
        // }, 1000); // 1 second delay (adjust as needed)
        
        e.preventDefault();
        return;
      }
    }
    
    // Handle Escape to exit scan mode
    if (e.key === "Escape" && scanMode) {
      setScanMode(false);
      setIsScanning(false); // Scanner loader भी बंद करें
      return;
    }

    // Scan mode में keyboard navigation disable करें
    if (scanMode) return;

    if (!showSuggestions || filteredData.length === 0) return;

    if (e.key === "Enter") {
      // Handle Enter for suggestions
      if (!showSuggestions || filteredData.length === 0) return;
      
      e.preventDefault();
      
      // Handle case when there's only one option
      if (filteredData.length === 1) {
        handleItemClick(filteredData[0]);
      } 
      // Handle case when there's a selected index
      else if (selectedIndex >= 0 && selectedIndex < filteredData.length) {
        handleItemClick(filteredData[selectedIndex]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIndex = selectedIndex + 1;
      const newIndex = nextIndex < filteredData.length ? nextIndex : 0;
      setSelectedIndex(newIndex);
      scrollIntoView(newIndex);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prevIndex = selectedIndex - 1;
      const newIndex = prevIndex >= 0 ? prevIndex : filteredData.length - 1;
      setSelectedIndex(newIndex);
      scrollIntoView(newIndex);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setForceShowAll(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (!scanMode) {
      onChange(newValue);
    }
    
    setSelectedIndex(-1);
    setForceShowAll(false);
    setIsTyping(true);
    
    // Scan mode में suggestions न खोलें
    if (!scanMode && !showSuggestions) {
      setShowSuggestions(true);
    }
  };

  const defaultRenderSuggestion = (item, index) => (
    <div
      className={`p-3 hover:bg-[#6e2d90] flex flex-col gap-1 cursor-pointer ${
        selectedIndex === index ? "bg-[#6e2d90] text-white" :
        isItemSelected(item) ? "bg-[#F5EBFB]" : ""
      }`}
      onClick={() => handleItemClick(item)}
      onMouseEnter={() => setSelectedIndex(index)}
      ref={(el) => (itemRefs.current[index] = el)}
    >
      <p className="font-medium">{getDefaultDisplayValue(item)}</p>
    </div>
  );

  const scrollIntoView = (index) => {
    if (itemRefs.current[index] && suggestionsRef.current) {
      const item = itemRefs.current[index];
      const container = suggestionsRef.current;
      const itemTop = item.offsetTop;
      const itemBottom = itemTop + item.offsetHeight;
      const containerTop = container.scrollTop;
      const containerBottom = containerTop + container.offsetHeight;

      if (itemTop < containerTop) {
        container.scrollTop = itemTop;
      } else if (itemBottom > containerBottom) {
        container.scrollTop = itemBottom - container.offsetHeight;
      }
    }
  };

  return (
    <div
      className="autocomplete-wrapper relative"
      ref={wrapperRef}
    >
      {/* Label */}
      <div className="mb-1">
        {label && (
          <label
            className={`mb-5 ${errorMessage ? "text-red-700 " : "text-[#000000]"}`}
          >
            {label} {isMandatory && <span className="text-red-700">*</span>}
            {infoText && (
              <div className="relative inline-flex group">
                <span 
                  onClick={() => { setShowInfoText(!showInfoText) }} 
                  onMouseEnter={() => { setShowInfoText(true) }} 
                  onMouseLeave={() => { setShowInfoText(false) }} 
                  className="inline-flex items-center justify-center ml-1 border border-[rgb(108,113,138)] text-[rgb(108,113,138)] rounded-full w-4 h-4 text-center leading-4 text-xs cursor-pointer"
                >
                  i
                </span>
                {showInfoText && (
                  <div
                    className={`
                      z-50 px-3 py-2 rounded whitespace-normal
                      ${isMobileView()
                        ? `fixed left-0 justify-center flex w-full bg-[#4A1E73] text-[#FFFFFF] top-0 }`
                        : `
                            absolute bg-[#4A1E73] text-[#FFFFFF] 
                            ${getTooltipPositionClasses(tooltipPosition)}
                            ${tooltipOffset ? `m-${tooltipOffset}` : ""}
                          `
                      }
                    `}
                    style={{
                      width: isMobileView() ? "100%" : tooltipWidth || undefined,
                    }}
                  >
                    {isMobileView() && (
                      <span
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-red-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowInfoText(false);
                        }}
                        aria-label="Close tooltip"
                      >
                        <span className="w-4">
                          <Icon type="remove" />
                        </span>
                      </span>
                    )}
                    {infoText}
                  </div>
                )}
              </div>
            )}
          </label>
        )}
      </div>

      {/* Input Container */}
      <div
        className={`h-[34px] ${disabled ? "bg-[#EEEEEE] cursor-not-allowed" : "bg-white"} flex justify-between items-center rounded-lg border ${
          errorMessage
            ? "border-red-700 "
            : scanMode 
              ? "border-[#408dfb] focus-within:shadow-[0_0_0_3px_rgba(64,_141,_251,_0.16)]"
              : "border-[#d7d5e2] hover:border-[#408dfb] focus-within:shadow-[0_0_0_3px_rgba(64,_141,_251,_0.16)]"
        }`}
      >
        {/* Search Icon */}
        {showsearch && (
          <span
            className="w-10 px-2 text-gray-400 hover:text-blue-600 cursor-pointer"
            onClick={searchbtnclick}
          >
            <Icon type="search" />
          </span>
        )}

        {/* Scan Button (Left side before input) */}
        {onScan && (
          <span
            className={`w-10 px-2 ${scanMode ? "text-blue-600" : "text-gray-400"} hover:text-blue-600 cursor-pointer`}
            onClick={toggleScanMode}
            title={scanMode ? "Exit scan mode" : "Enter scan mode"}
          >
            <Icon type={scanMode ? "barcode-active" : "barcode"} />
          </span>
        )}

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={scanMode ? "Start scanning to search products" : placeholder}
          className="w-full px-2 text-gray-700 border-none rounded-lg outline-none bg-none"
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoFocus={scanMode}
        />

        {/* Clear Button */}
        {(inputValue && !disabled) && (
          <span
            className="inline-block w-8 text-gray-400 hover:text-blue-600 pr-2 cursor-pointer bg-none"
            onClick={() => {
              onChange("");
              setInputValue("");
              setForceShowAll(true);
              setIsTyping(false);
              if (scanMode) {
                setIsScanning(false);
              }
            }}
          >
            <Icon type="remove" />
          </span>
        )}

        {/* Dropdown Toggle - Scan mode में hide करें */}
        {!disabled && !scanMode && (
          <span
            className="inline-block w-8 text-gray-400 hover:text-blue-600 pr-2 cursor-pointer bg-none"
            onClick={handleToggleSuggestions}
          >
            {showSuggestions && <Icon type="uparrow" />}
            {!showSuggestions && <Icon type="downarrow" />}
          </span>
        )}

        {/* Side Loader */}
        {showSideLoader && (
          <div className="pr-2">
            <span className="w-5 h-5 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-[#BE007B] border-solid border-t-transparent rounded-full animate-spin"></div>
            </span>
          </div>
        )}
        
        {/* Side Button */}
        {sideButton && (
          <span
            className="px-2 border-l border-[#d7d5e2] h-[34px] pt-1.5 text-gray-500 hover:text-blue-600 cursor-pointer text-nowrap"
            onClick={sideButtonClick}
          >
            {sideButton}
          </span>
        )}
      </div>

      {/* Error Message */}
      {errorMessage && <p className="mt-1 text-red-700">{errorMessage}</p>}

      {/* Scan Mode Indicator */}
      {scanMode && (
        <div className="absolute mt-1 left-0 right-0 flex items-center justify-center">
          <div className="bg-blue-100 border border-blue-300 text-blue-700 px-3 py-1 rounded-lg text-sm flex items-center gap-2 shadow-sm">
            <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
            <span className="font-medium">Scan Mode Active</span>
            <span className="text-xs">(Press Enter to scan, Esc to exit)</span>
          </div>
        </div>
      )}

      {/* Scanning Loader */}
      {isScanning && (
        <div className="absolute mt-1 left-0 right-0 flex items-center justify-center z-20">
          <div className="bg-white border border-blue-300 text-blue-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2 shadow-lg">
            <div className="w-5 h-5 border-2 border-blue-500 border-solid border-t-transparent rounded-full animate-spin"></div>
            <span className="font-medium">Scanning...</span>
            <span className="text-xs">Please wait</span>
          </div>
        </div>
      )}

      {/* Suggestions Dropdown - Scan mode में न दिखाएं */}
      {showSuggestions && !scanMode && (
        <div 
          className={`absolute w-full z-10 rounded-lg shadow-lg bg-white max-h-60 ${
            dropdownDirection === 'down' ? 'mt-1.5' : 'mb-1.5 bottom-full'
          }`}
          style={{
            maxHeight: dropdownDirection === 'up' 
              ? `${Math.min(window.innerHeight * 0.4, inputRect.top - 20)}px` 
              : `${Math.min(window.innerHeight * 0.4, window.innerHeight - inputRect.bottom - 60)}px`
          }}
        >
          <div className="relative z-10 mt-1.5 w-full rounded-lg shadow-lg bg-white max-h-60 flex flex-col">
            <div
              className="flex-1 overflow-y-auto"
              ref={suggestionsRef}
            >
              {filteredData.map((item, index) => (
                <div
                  key={index}
                  className={`p-3 flex justify-between items-center gap-1 cursor-pointer ${
                    selectedIndex === index ? "bg-[#6e2d90] text-white" :
                    isItemSelected(item) ? "bg-[#F5EBFB]" : ""
                  }`}
                  onClick={() => handleItemClick(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  ref={(el) => (itemRefs.current[index] = el)}
                >
                  <div className="flex flex-col gap-1">
                    {renderSuggestion
                      ? renderSuggestion(item, index)
                      : defaultRenderSuggestion(item, index)}
                  </div>
                  {isItemSelected(item) && (
                    <span className="w-3 text-[#6e2d90]">
                      <Icon type="check" />
                    </span>
                  )}
                </div>
              ))}
              {filteredData.length === 0 && (
                <div className="p-3 text-gray-500">
                  No results found
                </div>
              )}
            </div>

            {Addbutntext && (
              <div className="border-t border-gray-200 hover:bg-gray-100 p-3">
                <span
                  className="flex items-center text-[#6e2d90] cursor-pointer"
                  onClick={Addbuttonclick}
                >
                  <span className="w-10">
                    <Icon type="add" />
                  </span>
                  <span className="ml-2">{Addbutntext}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Autocompletesearch;